/**
 * Asynchronous processing.
 *
 * @callback Runnable
 * @param {number} RequestID
 * @return {Promise<any>}
 */

let serialID = 0;
export class TaskQueue {
  /** Asynchronous queue of requests */
  #requests = [];

  /**
   * Adds a request to the sequential processing queue. Can be dropped if new request is made before it starts.
   * @param {Runnable} runnable
   * @return {Promise<any>}
   */
  enqueue(runnable) {
    let resolve, reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    const request = {
      id: serialID++,
      promise,
      drop: () => reject(new TaskDroppedError(request.id))
    };

    // It is not started automatically, to allow sequential processing and evicting of outdated inactive requests.
    request.start = () => {
      runnable(request.id).
      then(resolve).
      catch(reject).
      finally(() => {
        this.#requests.shift();
        if (this.#requests.length) {
          this.#requests[0].start();
        }
      });
    };

    // Since requests are sequential only the first is active, drops all inactive.
    while (this.#requests.length > 1) this.#requests.pop().drop();

    // Start if first in line.
    if (this.#requests.push(request) === 1) request.start();

    return promise;
  }

  /** Drop all requests. */
  dispose() {
    while (this.#requests.length > 0) {
      this.#requests.pop().drop();
    }
  }

  /** Current size of the task queue. */
  get length() {
    return this.#requests.length;
  }
}

export class TaskDroppedError extends Error {
  constructor(taskId) {
    super('dropped ' + taskId);
    this.taskId = taskId;
  }
}